import { Request, Response, NextFunction, ErrorRequestHandler, RequestHandler } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ErrorCode, ErrorHandlingOptions } from './types';
import { getConfig } from './config';

/**
 * Custom error class with status code and error code
 */
export class HttpError extends Error {
  statusCode: number;
  code: string;
  details?: Record<string, any>;

  constructor(
    message: string,
    statusCode: number = 500,
    code: string | ErrorCode = ErrorCode.INTERNAL_ERROR,
    details?: Record<string, any>
  ) {
    super(message);
    this.name = 'HttpError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;

    // Capture stack trace
    Error.captureStackTrace(this, this.constructor);
  }
}

/**
 * Create error handler middleware
 * @param options Error handler options
 * @param logger Logger instance
 * @returns Express error middleware
 */
export function errorHandlerMiddleware(
  options: ErrorHandlingOptions = {},
  logger: Logger
): ErrorRequestHandler {
  // Get middleware configuration
  const config = getConfig();

  const includeStack = options.includeStack ?? config.errors?.includeStack ?? (process.env.NODE_ENV !== 'production');
  const logErrors = options.logErrors ?? config.errors?.logErrors ?? true;
  const defaultMessage = options.defaultMessage ?? config.errors?.defaultMessage ?? 'An unexpected error occurred';
  const defaultCode = options.defaultCode ?? config.errors?.defaultCode ?? ErrorCode.INTERNAL_ERROR;

  return (err: any, req: Request, res: Response, next: NextFunction) => {
    // Default status code and error code
    const statusCode = err.statusCode || 500;
    const errorCode = err.code || defaultCode;
    const message = err.message || defaultMessage;
    const details = err.details || undefined;

    // Log the error
    if (logErrors) {
      const logData: Record<string, any> = {
        statusCode,
        errorCode,
        path: req.path,
        method: req.method
      };

      // Add correlation ID if available
      if ((req as any).correlationId) {
        logData.correlationId = (req as any).correlationId;
      }

      // Add stack trace if available and in development
      if (err.stack && process.env.NODE_ENV !== 'production') {
        logData.stack = err.stack;
      }

      if (statusCode >= 500) {
        logger.error(`Server error: ${message}`, logData);
      } else {
        logger.warn(`Client error: ${message}`, logData);
      }
    }

    // Prepare error response
    const errorResponse: Record<string, any> = {
      message,
      code: errorCode,
      statusCode
    };

    // Add stack trace if enabled
    if (includeStack && err.stack) {
      errorResponse.stack = err.stack;
    }

    // Add additional details if available
    if (details) {
      errorResponse.details = details;
    }

    // Add timestamp
    errorResponse.timestamp = new Date().toISOString();

    // Send error response
    res.status(statusCode).json({
      error: errorResponse
    });
  };
}

/**
 * Apply error handler to the application
 * Adds a 404 handler and the main error handler
 * @param app Express application
 * @param logger Logger instance
 */
export function applyErrorHandler(app: any, logger: Logger): void {
  // Add a catch-all for 404 errors
  app.use((req: Request, res: Response, next: NextFunction) => {
    next(new HttpError(
      `Route not found: ${req.method} ${req.path}`,
      404,
      ErrorCode.NOT_FOUND
    ));
  });

  // Apply the error handler middleware
  app.use(errorHandlerMiddleware({}, logger));
}

/**
 * Wrapper for async request handlers to catch errors
 * @param fn Async request handler
 * @returns Express middleware that catches errors
 */
export function asyncHandler(fn: Function): RequestHandler {
  return (req: Request, res: Response, next: NextFunction) => {
    Promise.resolve(fn(req, res, next)).catch(next);
  };
}
